/**
 * Manages the registration of the service worker and web push notifications,
 * or, in the iOS app, its notifications through APNs (PushNotifications.swift).
 */
import * as deviceDetect from "deviceDetect";
import * as iOSBridge from "iOSBridge";
import readlangClient from "readlangClient";

type DeviceSubscriptionStatus =
  | {
      type: "haveSubscription";
      subscription: any;
      device: string;
    }
  | {
      type: "noSubscription";
    }
  | {
      type: "notSupported";
    };

type Permission = "granted" | "denied" | "default" | "notSupported";

/** The iOS app's registration with APNs, as the server stores it */
export interface AppSubscriptionData {
  apnsToken: string;
  apnsEnvironment: "production" | "sandbox";
}

export interface ServerSubscription {
  _id: string;
  userID: string;
  subscriptionData:
    | {
        endpoint: string;
        keys: {
          auth: string;
          p256dh: string;
        };
      }
    | AppSubscriptionData;
  device: string;
}

// __VAPID_PUBLIC_KEY is injected by rollup from the corresponding environment variable
// @ts-ignore
const vapidPublicKey = __VAPID_PUBLIC_KEY;

// Not in an iframe such as the Web Reader toolbar: browsers partition its
// storage by the site it's on, so registering there would add a service worker
// for every site read (or fail where third-party service workers are blocked).
const isWebPushSupported = () =>
  !deviceDetect.isIOSApp() &&
  window.self === window.top &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  !/YandexBot|Baiduspider/.test(navigator.userAgent);

/** Whether this is the iOS app, with its own notifications */
export const isApp = deviceDetect.isIOSApp;

const isSupported = () => isWebPushSupported() || isApp();

/**
 * The service worker's registration: null where web push isn't supported, or
 * where fetching its script failed (flaky networks, crawlers), as push then
 * isn't available
 */
const serviceWorkerRegistration: Promise<ServiceWorkerRegistration | null> =
  (async () => {
    if (!isWebPushSupported()) {
      return null;
    }
    try {
      const registration = await navigator.serviceWorker.register(
        `/serviceWorker.js?cacheBust=${window.READLANG.gitCommit}`
      );
      // Fetch any newer version; failing that, the current one carries on
      registration.update().catch((error) => {
        console.warn("Couldn't update the service worker: ", error);
      });
      return registration;
    } catch (error) {
      console.warn("Couldn't register the service worker: ", error);
      return null;
    }
  })();

/** Why `subscribe` failed: the user hasn't allowed notifications */
export const deniedMessage = () =>
  isApp()
    ? "Notifications are turned off for Readlang. You can turn them on in the Settings app, under Apps → Readlang → Notifications."
    : "Sorry, push notifications were denied. Please check your browser settings to enable them for readlang.com.";

/** @returns true if succeeded, false if not */
export const subscribe = async (device): Promise<boolean> => {
  let subscriptionData;
  try {
    subscriptionData =
      (await iOSBridge.get()?.registerForPushNotifications()) ??
      (await (
        await serviceWorkerRegistration
      ).pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey,
      }));
  } catch (error) {
    console.error("Error subscribing to push notifications: ", error);
    return false;
  }

  await readlangClient.request("POST", "/api/webPushNotifications/subscribe", {
    subscriptionData,
    device,
  });

  return true;
};

export const reactivateSubscription = async (subscription) => {
  return await readlangClient.request(
    "POST",
    "/api/webPushNotifications/subscribe",
    subscription
  );
};

export const getSubscriptionStatus =
  async (): Promise<DeviceSubscriptionStatus> => {
    if (!isSupported()) {
      return { type: "notSupported" };
    }

    const app = iOSBridge.get();
    if (app) {
      const subscription = await app.pushNotificationStatus();
      return subscription
        ? {
            type: "haveSubscription",
            subscription,
            device: deviceDetect.deviceString(),
          }
        : { type: "noSubscription" };
    }

    const registration = await serviceWorkerRegistration;
    if (!registration) {
      return { type: "notSupported" };
    }

    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      return { type: "noSubscription" };
    }

    return {
      type: "haveSubscription",
      subscription: subscription.toJSON(),
      device: deviceDetect.deviceString(),
    };
  };

export const getServerSubscriptions = async (
  thisDeviceStatus: DeviceSubscriptionStatus
): Promise<(ServerSubscription & { thisDevice?: boolean })[]> => {
  const subscriptions = (
    await readlangClient.request(
      "GET",
      "/api/webPushNotifications/subscriptions",
      {}
    )
  ).data.subscriptions as (ServerSubscription & { thisDevice?: boolean })[];

  if (thisDeviceStatus.type === "haveSubscription") {
    // Look for a subscription matching this device and mark it as such
    const thisDevice = thisDeviceStatus.subscription;
    for (const subscription of subscriptions) {
      const subscriptionData = subscription.subscriptionData;
      if (
        "apnsToken" in subscriptionData
          ? subscriptionData.apnsToken === thisDevice.apnsToken
          : subscriptionData.endpoint === thisDevice.endpoint &&
            subscriptionData.keys.auth === thisDevice.keys?.auth &&
            subscriptionData.keys.p256dh === thisDevice.keys?.p256dh
      ) {
        subscription.thisDevice = true;
      }
    }
  }

  return subscriptions;
};

export const sendNotification = async (
  title: string,
  body: string,
  badgeCount: number,
  path?: string
) => {
  await readlangClient.request(
    "POST",
    "/api/webPushNotifications/sendNotification",
    { title, body, icon: "/favicon-196.png", badgeCount, path }
  );
};

export const currentPermission = (): Permission => {
  return isWebPushSupported() ? Notification.permission : "notSupported";
};

export const enableWebNotifications = async () => {
  return await Notification.requestPermission();
};

export const removeSubscription = async (subscriptionID: string) => {
  await readlangClient.request(
    "DELETE",
    `/api/webPushNotifications/subscription/${subscriptionID}`,
    {}
  );
};
